import React from 'react';
import { getTagText, type Tag } from '../../../utils/tagUtils';
import 'primeicons/primeicons.css';
import './ContractExpirationCard.styles.scss';

export interface ContractExpirationCardProps {
  title?: string;
  date: string;
  contract_number?: string;
  contract_owner?: string;
  agency?: string;
  contract_type?: string;
  ndcs?: string[];
  tags?: Tag[];
  priority?: 'high' | 'medium' | 'normal';
  url?: string;
  onCardClick?: () => void;
}

const ContractExpirationCard: React.FC<ContractExpirationCardProps> = ({
  title,
  date,
  contract_number,
  contract_owner,
  agency,
  contract_type,
  ndcs,
  tags,
  priority,
  url,
  onCardClick,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCardClick) {
      onCardClick();
    }
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onCardClick) {
        onCardClick();
      }
    }
  };

  // Render the formatted description with labeled fields
  const renderDescription = () => {
    return (
      <div className="contract-details">
        {contract_owner && (
          <div className="detail-row">
            <span className="detail-label">Contract Owner: </span>
            <span className="detail-value">{contract_owner}</span>
          </div>
        )}
        {contract_number && (
          <div className="detail-row">
            <span className="detail-label">Contract Number:</span>
            <span className="detail-value">{contract_number}</span>
          </div>
        )}
        {agency && (
          <div className="detail-row">
            <span className="detail-label">Agency:</span>
            <span className="detail-value">{agency}</span>
          </div>
        )}
        {contract_type && (
          <div className="detail-row">
            <span className="detail-label">Contract Type:</span>
            <span className="detail-value">{contract_type}</span>
          </div>
        )}
        {ndcs && ndcs.length > 0 && (
          <div className="detail-row ndcs-row">
            <span className="detail-label">NDC:</span>
            <span className="detail-value ndcs-value">{ndcs.join(', ')}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`contract-expiration-card priority-${priority}`}
      tabIndex={0}
      role="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="card-header">
        <div className="card-title">{title}</div>
        <div className="card-date">{date}</div>
      </div>
      <div className="card-description">{renderDescription()}</div>
      <div className="card-tags">
        {tags &&
          tags.map((tag, index) => (
            <span key={index} className="card-tag">
              {getTagText(tag)}
            </span>
          ))}
      </div>
      {url && (
        <button
          className="card-link-icon"
          onClick={handleLinkClick}
          aria-label="Open link in new tab"
          title="Open link in new tab"
        >
          <i className="pi pi-external-link"></i>
        </button>
      )}
    </div>
  );
};

export default ContractExpirationCard;
